import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, ShieldCheck, AlertCircle, CheckCircle2, Loader, RefreshCw } from 'lucide-react';
import API_URL from '../../config';
import '../../pages/events/Events.css';
import '../../pages/events/EventsPrototype.css';

export default function ExternalAuthModal({ isOpen, onClose, onSuccess, eventSlug = '', eventName }) {
  const [step, setStep] = useState(1); // 1 = Enter email, 2 = Enter OTP
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cooldown, setCooldown] = useState(0);
  const [infoMessage, setInfoMessage] = useState(null);
  const inputRefs = useRef([]);

  // Resend cooldown timer
  useEffect(() => {
    let timer;
    if (cooldown > 0) {
      timer = setInterval(() => setCooldown(c => c - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [cooldown]);

  // Reset state on open/close
  useEffect(() => {
    if (isOpen) {
      setError(null);
      setInfoMessage(null);
      setStep(1);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSendOtp = async (e) => {
    if (e) e.preventDefault();
    setError(null);
    setInfoMessage(null);

    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) {
      setError('Please enter your email address.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(cleanEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    if (cleanEmail.endsWith('@vnrvjiet.in')) {
      setError('VNR VJIET students and faculty should sign in with Google VJLINK.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/events-external-auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: cleanEmail, 
          event_slug: eventSlug,
          event_name: eventName 
        })
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to send verification code. Please try again.');
        return;
      }

      setStep(2);
      setCooldown(60);
      setOtp(['', '', '', '', '', '']);
      if (data.dev_code) {
        setInfoMessage(`[Dev Mode] Verification code: ${data.dev_code}`);
      } else {
        setInfoMessage('Verification code sent! Please check your inbox (and spam folder).');
      }
      setTimeout(() => {
        if (inputRefs.current[0]) inputRefs.current[0].focus();
      }, 100);
    } catch (err) {
      console.error(err);
      setError('Network error. Unable to reach authentication service.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) {
      // Handle paste of multiple digits
      const pasted = value.replace(/\D/g, '').slice(0, 6);
      if (pasted.length > 0) {
        const nextOtp = [...otp];
        for (let i = 0; i < 6; i++) {
          nextOtp[i] = pasted[i] || '';
        }
        setOtp(nextOtp);
        const nextFocus = Math.min(pasted.length, 5);
        if (inputRefs.current[nextFocus]) inputRefs.current[nextFocus].focus();
        if (pasted.length === 6) {
          verifyOtpCode(pasted);
        }
      }
      return;
    }

    const digit = value.replace(/\D/g, '');
    const nextOtp = [...otp];
    nextOtp[index] = digit;
    setOtp(nextOtp);

    // Auto-advance to next input
    if (digit && index < 5 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1].focus();
    }

    // Auto-submit if all 6 digits entered
    const completeCode = nextOtp.join('');
    if (completeCode.length === 6 && !nextOtp.includes('')) {
      verifyOtpCode(completeCode);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0 && inputRefs.current[index - 1]) {
      inputRefs.current[index - 1].focus();
    }
  };

  const verifyOtpCode = async (codeToVerify) => {
    const fullCode = codeToVerify || otp.join('');
    if (fullCode.length !== 6) {
      setError('Please enter all 6 digits of your verification code.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/events-external-auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), otp: fullCode, event_slug: eventSlug })
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Verification failed. Please check the code.');
        return;
      }

      // Securely store external session token and user identity
      localStorage.setItem('convergence_external_token', data.session_token);
      localStorage.setItem('convergence_external_user', JSON.stringify(data.user));

      if (onSuccess) {
        onSuccess(data.user, data.profile_completed, data.session_token);
      }
      onClose();
    } catch (err) {
      console.error(err);
      setError('Verification failed due to a connection error.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="vp-modal-backdrop open" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="vp-modal" role="dialog" aria-modal="true" style={{ maxWidth: '480px' }}>
        {/* Modal Head */}
        <div className="vp-modal-head">
          <div>
            <div className="vp-eyebrow">
              <ShieldCheck size={12} style={{ display: 'inline', marginRight: 4 }} />
              {eventName ? `${eventName} · Guest Access` : 'VJLINK Networking Hub · Event Access'}
            </div>
            <h2 style={{ fontSize: '22px' }}>
              {step === 1 ? 'Join the room.' : 'Verify your email.'}
            </h2>
          </div>
          <button className="vp-icon-btn" onClick={onClose} aria-label="Close modal">
            <X size={18} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="vp-modal-body">
          <p className="vp-step-copy" style={{ marginBottom: '18px' }}>
            {step === 1
              ? `For participants outside VNR VJIET. Sign in with a secure one-time verification code to explore ${eventName || 'the networking hub'}.`
              : `Enter the 6-digit verification code sent to ${email}.`}
          </p>

          {error && (
            <div style={{
              background: '#fff0ee',
              border: '1px solid #ffd2cd',
              borderRadius: '12px',
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              color: '#a53b31',
              fontSize: '13px',
              marginBottom: '18px'
            }}>
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {infoMessage && (
            <div style={{
              background: 'var(--vp-blue-soft)',
              border: '1px solid rgba(90,105,232,0.2)',
              borderRadius: '12px',
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              color: 'var(--vp-blue)',
              fontSize: '13px',
              marginBottom: '18px'
            }}>
              <CheckCircle2 size={16} />
              <span>{infoMessage}</span>
            </div>
          )}

          {step === 1 ? (
            <form onSubmit={handleSendOtp}>
              <div className="vp-field">
                <label>Your email address</label>
                <input 
                  type="email" 
                  value={email} 
                  onChange={e => setEmail(e.target.value)} 
                  placeholder="e.g. you@college.edu or gmail.com" 
                  autoFocus 
                  required 
                />
              </div>
              <p style={{ fontSize: '11px', color: 'var(--vp-muted)', margin: '12px 0 0' }}>
                Your account will be scoped strictly to this event in the system.
              </p>
            </form>
          ) : (
            <div>
              <div className="vp-field">
                <label>6-Digit Verification Code</label>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', margin: '10px 0' }}>
                  {otp.map((digit, i) => (
                    <input
                      key={i}
                      ref={el => inputRefs.current[i] = el}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={e => handleOtpChange(i, e.target.value)}
                      onKeyDown={e => handleKeyDown(i, e)}
                      style={{
                        width: '46px',
                        height: '52px',
                        textAlign: 'center',
                        fontSize: '20px',
                        fontWeight: 800,
                        padding: 0,
                        borderRadius: '12px',
                        border: '1px solid var(--vp-line)',
                        background: 'var(--vp-paper)'
                      }}
                    />
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', fontSize: '12px' }}>
                <button
                  type="button"
                  className="vp-btn vp-btn-small vp-btn-outline"
                  onClick={() => { setStep(1); setError(null); }}
                >
                  Change email
                </button>

                <button
                  type="button"
                  className="vp-btn vp-btn-small vp-btn-soft"
                  onClick={handleSendOtp}
                  disabled={cooldown > 0 || loading}
                >
                  {cooldown > 0 ? (
                    `Resend in ${cooldown}s`
                  ) : (
                    <>
                      <RefreshCw size={12} /> Resend code
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Modal Foot */}
        <div className="vp-modal-foot">
          <button type="button" className="vp-btn vp-btn-outline" onClick={onClose}>
            Cancel
          </button>

          {step === 1 ? (
            <button 
              type="button" 
              className="vp-btn vp-btn-dark" 
              onClick={handleSendOtp}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader size={14} className="vp-spin" /> Sending...
                </>
              ) : (
                'Send verification code →'
              )}
            </button>
          ) : (
            <button 
              type="button" 
              className="vp-btn vp-btn-dark" 
              onClick={() => verifyOtpCode()}
              disabled={loading || otp.join('').length !== 6}
            >
              {loading ? (
                <>
                  <Loader size={14} className="vp-spin" /> Verifying...
                </>
              ) : (
                'Verify & continue →'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
